import {createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  extractTableDataForSuggestions,
  GetTableDataFn,
} from 'notebook/ai-tools/suggest-plots/extractTableDataForSuggestions';
import SuggestedPlotsHoverOption from 'notebook/ai-tools/suggest-plots/SuggestedPlotsHoverOption';
import {arePlotSuggestionsSupportedSync} from 'notebook/ai-tools/suggest-plots/suggestPlotsUtils';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import APIUtil from 'util/APIUtil';

import {
  ActionType,
  CREATE_DATASET_BUTTON_TITLE,
  CreateDatasetFromNotebookParams,
  EXISTING_ANALYSIS_OPTION,
  handleClickActionButton,
  isButtonDisabled,
  NEW_ANALYSIS_OPTION,
  structuredTableToPlainTextTable,
} from './createDatasetActionFunction';
import makeCustomButtonActionFunction from './makeCustomButtonActionFunction';

interface DatasetAndPlotsButtonProps {
  params: CreateDatasetFromNotebookParams<unknown, unknown>;
}

function DatasetAndPlotsButton({params}: DatasetAndPlotsButtonProps): JSX.Element {
  const {classes} = useStyles();
  const showPlots = arePlotSuggestionsSupportedSync();
  const [isOpen, setIsOpen] = useState(false);

  const disabled = isButtonDisabled(params);

  const entryId = params.entryOrTemplateId;

  const supportsAIContext = entryId != null && (APIUtil.isEntry(entryId) || APIUtil.isDocument(entryId));

  const getTableData: GetTableDataFn = useCallback(() => {
    const table = params.structuredTableAdapter.toTable(params.structuredTable);
    return extractTableDataForSuggestions(table, params.computedTable);
  }, [params.structuredTableAdapter, params.structuredTable, params.computedTable]);

  const handleAction = (actionType: ActionType): void => {
    handleClickActionButton({...params, structuredTableToPlainTextTable}, actionType);
    setIsOpen(false);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Tooltip title={CREATE_DATASET_BUTTON_TITLE}>
      <Dropdown
        isOpen={isOpen}
        onToggle={setIsOpen}
        closeOnInsideClick={false}
        menu={
          <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
            <Option
              onSelect={() => {
                handleAction(ActionType.NEW_ANALYSIS);
              }}
              shouldEllipsize={true}
            >
              {NEW_ANALYSIS_OPTION}
            </Option>
            <Option
              onSelect={() => {
                handleAction(ActionType.EXISTING_ANALYSIS);
              }}
              shouldEllipsize={true}
            >
              {EXISTING_ANALYSIS_OPTION}
            </Option>
            {showPlots && supportsAIContext && (
              <OptionContainer shouldEllipsize={true}>
                <SuggestedPlotsHoverOption
                  entryId={entryId}
                  tableName={params.structuredTable.name}
                  getTableData={getTableData}
                  onSuggestionChosen={handleClose}
                />
              </OptionContainer>
            )}
          </Menu>
        }
      >
        <Button
          disabled={disabled}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className={classes.button}
          icon={ModelTypeConstants.ANALYSIS}
          iconAltText={CREATE_DATASET_BUTTON_TITLE}
          isDropdown={true}
          dataTestAttrs={{
            component: 'CreateAnalysisButton',
            element: 'button',
          }}
        />
      </Dropdown>
    </Tooltip>
  );
}

const useStyles = createStyles(() => ({
  button: {
    marginLeft: 6,
  },
}));

export const createDatasetWithPlotsActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: (params: CreateDatasetFromNotebookParams<unknown, unknown>) => (
    <DatasetAndPlotsButton params={params} />
  ),
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
  getHidden: (params: CreateDatasetFromNotebookParams<unknown, unknown>) =>
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS ||
    APIUtil.isStageEntry(params.entryOrTemplateId ?? '') ||
    APIUtil.isStageEntryTemplate(params.entryOrTemplateId ?? ''),
});
